import { useState, useEffect } from "react";
import api from "../../services/api";
import { Handshake, Trash2, Edit, Save, Search } from 'lucide-react';
// 👇 Common Layout and PageTitle imports
import AdminLayout from '../layouts/AdminLayout';
import PageTitle from '../components/ui/PageTitle';

export default function ManagePartners() {
    const [partners, setPartners] = useState([]);
    const [name, setName] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [status, setStatus] = useState('active');
    const [logoFile, setLogoFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    const fetchPartners = async () => {
        try {
            const response = await api.get('/partners');
            if (response.data.status === 'success') {
                setPartners(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching partners:', error);
        }
    };

    useEffect(() => {
        fetchPartners();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', name);
        if (websiteUrl) formData.append('website_url', websiteUrl);
        formData.append('status', status);
        if (logoFile) formData.append('logo', logoFile);

        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');

        setLoading(true);
        try {
            if (isEditing) {
                await api.post(`/partners/${editId}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${token}`
                    }
                });
                setMessage('Partner updated successfully!');
            } else {
                if (!logoFile) {
                    alert('Please select a logo image.');
                    setLoading(false);
                    return;
                }
                await api.post('/partners', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${token}`
                    }
                });
                setMessage('Partner added successfully!');
            }

            setName('');
            setWebsiteUrl('');
            setStatus('active');
            setLogoFile(null);
            setIsEditing(false);
            setEditId(null);
            fetchPartners();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error saving partner:', error);
            alert('Failed to save partner. Check inputs.');
        } finally {
            setLoading(false);
        }
    };

    // 🌟 Direct Toggle Status Function from Table
    const handleToggleStatus = async (partner) => {
        const newStatus = partner.status === 'active' ? 'inactive' : 'active';
        const formData = new FormData();
        formData.append('name', partner.name);
        if (partner.website_url) formData.append('website_url', partner.website_url);
        formData.append('status', newStatus);

        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        try {
            await api.post(`/partners/${partner.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });
            fetchPartners();
            setMessage(`Partner status changed to ${newStatus}!`);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error toggling status:', error);
            alert('Failed to change status.');
        }
    };

    const handleEdit = (partner) => {
        setIsEditing(true);
        setEditId(partner.id);
        setName(partner.name);
        setWebsiteUrl(partner.website_url || '');
        setStatus(partner.status || 'active');
        setLogoFile(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditId(null);
        setName('');
        setWebsiteUrl('');
        setStatus('active');
        setLogoFile(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this partner?')) return;

        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        try {
            await api.delete(`/partners/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchPartners();
            setMessage('Partner deleted successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error deleting partner:', error);
        }
    };

    const filteredPartners = partners.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 👇 Wrapped the whole UI inside AdminLayout
    return (
        <AdminLayout>
            <div className="max-w-7xl w-full mx-auto space-y-8">
                
                {/* 👇 Common PageTitle Component */}
                <div className="mb-2">
                    <PageTitle 
                        title="Manage Partners" 
                        subtitle="Add and manage partner logos and details." 
                    />
                </div>

                {message && (
                    <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm font-medium flex items-center shadow-sm">
                        {message}
                    </div>
                )}

                {/* Add / Edit Partner Form Box */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
                        <Handshake className="text-[#FED547]" size={22} />
                        <h3 className="font-bold text-gray-900 dark:text-white text-base">
                            {isEditing ? 'Edit Partner Logo' : 'Add New Partner'}
                        </h3>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-2">Partner Name *</label>
                                <input 
                                    type="text" 
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                    required 
                                    placeholder="Enter partner name"
                                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FED547] focus:border-[#FED547] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-2">Website URL (Optional)</label>
                                <input 
                                    type="url" 
                                    value={websiteUrl} 
                                    onChange={(e) => setWebsiteUrl(e.target.value)} 
                                    placeholder="https://example.com"
                                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FED547] focus:border-[#FED547] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-2">Status *</label>
                                <select 
                                    value={status} 
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FED547] focus:border-[#FED547] dark:bg-gray-700 dark:border-gray-600 dark:text-white bg-white"
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-2">
                                    Logo Image {isEditing ? '(Leave blank to keep existing)' : '*'}
                                </label>
                                <input 
                                    type="file" 
                                    onChange={(e) => setLogoFile(e.target.files[0])} 
                                    required={!isEditing}
                                    className="w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100 cursor-pointer"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                {isEditing && (
                                    <button 
                                        type="button"
                                        onClick={handleCancelEdit}
                                        className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl text-sm hover:bg-gray-200 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button 
                                    type="submit" 
                                    disabled={loading}
                                    className="flex items-center gap-2 bg-[#FED547] text-black px-6 py-2.5 rounded-xl font-bold hover:bg-[#e5be3c] transition-colors shadow-sm text-sm disabled:opacity-70"
                                >
                                    <Save size={18} /> {loading ? 'Saving...' : (isEditing ? 'Update Partner' : 'Save Partner')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Partner List Table Box */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <h3 className="font-bold text-gray-900 dark:text-white text-lg">Partner List</h3>
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input 
                                type="text" 
                                placeholder="Search partner name..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-1 focus:ring-[#FED547] focus:border-[#FED547]"
                            />
                        </div>
                    </div>

                    {filteredPartners.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 text-sm">
                            No partners found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-[#FED547] text-black text-xs uppercase tracking-wider font-bold">
                                    <tr>
                                        <th className="py-4 px-6 w-16">S.NO</th>
                                        <th className="py-4 px-6">LOGO</th>
                                        <th className="py-4 px-6">PARTNER DETAILS</th>
                                        <th className="py-4 px-6">STATUS</th>
                                        <th className="py-4 px-6 text-center">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                                    {filteredPartners.map((partner, index) => (
                                        <tr key={partner.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="py-4 px-6 font-semibold">{index + 1}</td>
                                            <td className="py-4 px-6">
                                                <div className="w-20 h-12 bg-white border border-gray-100 rounded-lg flex items-center justify-center p-1.5 shadow-sm">
                                                    <img src={partner.logo_url} alt={partner.name} className="max-h-full max-w-full object-contain" />
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="font-bold text-gray-900 text-base">{partner.name}</div>
                                                <a href={partner.website_url || '#'} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate max-w-xs block mt-0.5">
                                                    {partner.website_url || 'No website provided'}
                                                </a>
                                            </td>
                                            
                                            {/* 🌟 Interactive Toggle Switch Button */}
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleStatus(partner)}
                                                        className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                                                            partner.status === 'active' || !partner.status ? 'bg-green-500' : 'bg-gray-300'
                                                        }`}
                                                    >
                                                        <span
                                                            className={`pointer-events-none inline-block h-3.5 w-3.5 mt-0.5 ml-0.5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                                                                partner.status === 'active' || !partner.status ? 'translate-x-5' : 'translate-x-0'
                                                            }`}
                                                        />
                                                    </button>
                                                    <span className={`text-xs font-bold ${partner.status === 'active' || !partner.status ? 'text-green-600' : 'text-gray-400'}`}>
                                                        {partner.status === 'active' || !partner.status ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="py-4 px-6 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button 
                                                        onClick={() => handleEdit(partner)}
                                                        className="p-1.5 text-gray-400 hover:text-yellow-500 rounded"
                                                        title="Edit Partner"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDelete(partner.id)}
                                                        className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                                                        title="Delete Partner"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}